import type { PhaseSpermiogramInput } from "./phase-request-mappers";

/**
 * Which spermiogram row a treatment step means, when a phase holds several.
 *
 * A phase can carry more than one spermiogram — a repeat sample, or the
 * clinic's record beside the patient's own — and the phases response promises
 * no ordering for that array. Reading `spermiograms[0]` therefore showed an
 * arbitrary row and, worse, let a patient write carry the id of a
 * provider-authored row and PATCH it. Every step that touches a spermiogram
 * (sperm collection, insemination, the fertilization step of embryo freezing,
 * and the semen analysis) picks its row through these two selectors instead.
 *
 * Rows are told apart by `source`, which the API stamps from the writing actor,
 * and by `semenSpecimenId`, which the collection and insemination steps set when
 * the patient picks a stored sample.
 */

type SpermiogramRowRead = {
  id?: string;
  source?: string | null;
  semenSpecimenId?: string | null;
  createdAt?: string | Date | null;
};

/**
 * Identity fields supplied by the patient when a cycle step names a semen
 * specimen. Keep this mapper pure so the fertilization write path can be
 * checked without loading the React Native hooks module.
 */
export function buildSpermiogramIdentity(
  data: Record<string, string | number | boolean | null>,
  patientId: string,
): Pick<
  PhaseSpermiogramInput,
  "patientId" | "source" | "semenSpecimenId" | "origin" | "spermType"
> {
  const identity: Pick<
    PhaseSpermiogramInput,
    "patientId" | "source" | "semenSpecimenId" | "origin" | "spermType"
  > = { patientId, source: "patient" };
  if (typeof data.semenSpecimenId === "string") {
    identity.semenSpecimenId = data.semenSpecimenId;
  }
  if (data.spermSource === "partner" || data.spermSource === "donor") {
    identity.origin = data.spermSource;
  }
  if (data.spermType === "fresh" || data.spermType === "frozen") {
    identity.spermType = data.spermType;
  }
  return identity;
}

const createdOrder = (
  left: SpermiogramRowRead,
  right: SpermiogramRowRead,
): number =>
  String(left.createdAt ?? "").localeCompare(String(right.createdAt ?? ""));

/**
 * The row the step displays. The clinic's record wins: it is the measured or
 * recorded fact, hers is what she entered. Without a clinic row, the oldest of
 * hers, so a reorder between refetches cannot swap what she sees.
 */
export function spermiogramRowForDisplay<T extends SpermiogramRowRead>(
  rows: readonly T[] | undefined | null,
): T | undefined {
  if (!rows?.length) return undefined;
  return (
    rows.find((row) => row.source === "provider") ??
    [...rows].sort(createdOrder)[0]
  );
}

/**
 * The row a patient write may carry the id of: hers, and only ever hers.
 *
 * When the step names a stored sample, the row already linked to that sample is
 * the one being corrected; otherwise her oldest row is, so the collection and
 * insemination steps of one cycle keep sharing a single record of that sample.
 * `undefined` is not a failure: the write then omits `id` and the phase write
 * inserts a new row beside the clinic's. That is safe because the nested
 * spermiogram write is per item — update by `id`, insert without one, delete
 * only on an explicit marker — so it never replaces the collection.
 */
export function spermiogramRowOwnedByPatient<T extends SpermiogramRowRead>(
  rows: readonly T[] | undefined | null,
  semenSpecimenId?: string | null,
): T | undefined {
  const hers = (rows ?? [])
    .filter((row) => row.source === "patient")
    .sort(createdOrder);
  if (!hers.length) return undefined;
  if (semenSpecimenId) {
    const linked = hers.find((row) => row.semenSpecimenId === semenSpecimenId);
    if (linked) return linked;
  }
  return hers[0];
}
